import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { CheckCircle, Package, Eye, EyeOff, Mail } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { authService, promotionService } from '../services/api.js'
import { useAuthStore } from '../store/index.js'
import toast from 'react-hot-toast'

export function OrderSuccess() {
  const navigate    = useNavigate()
  const location    = useLocation()
  const qc          = useQueryClient()
  const orderId     = location.state?.orderId || null

  const handleTrackOrder = () => {
    // Invalidate orders cache so the list refreshes immediately on arrival
    qc.invalidateQueries(['orders', 'mine'])
    navigate('/orders')
  }

  return (
    <div className="min-h-screen bg-surface-dark flex flex-col items-center justify-center px-6 text-center"
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'var(--safe-bottom)' }}>

      <div className="w-24 h-24 rounded-full bg-green-600/20 border border-green-600/30 flex items-center justify-center mb-6">
        <CheckCircle size={44} className="text-green-400" />
      </div>

      <h1 className="font-heading text-white text-3xl mb-2">Order Placed!</h1>
      <p className="font-display text-gray-400 text-base mb-1">Your order has been confirmed</p>
      {orderId && (
        <p className="font-display font-bold text-gold text-lg mb-6">Order #{orderId}</p>
      )}

      <div className="w-full max-w-sm bg-surface-card rounded-2xl border border-surface-border p-4 mb-8 space-y-3">
        <div className="flex items-start gap-3">
          <Mail size={18} className="text-primary flex-shrink-0 mt-0.5" />
          <p className="font-display text-gray-300 text-sm text-left">
            A confirmation email has been sent to you with your order summary.
          </p>
        </div>
        <div className="flex items-start gap-3">
          <Package size={18} className="text-primary flex-shrink-0 mt-0.5" />
          <p className="font-display text-gray-300 text-sm text-left">
            Your order has been sent to our team and will be processed shortly.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 w-full max-w-sm">
        <button onClick={handleTrackOrder} className="btn-primary py-4 text-base">Track My Order</button>
        <button onClick={() => navigate('/')} className="btn-outline py-4 text-base">Continue Shopping</button>
      </div>
    </div>
  )
}


// ── Notification type metadata ─────────────────────────────────────────────
const NOTIF_META = {
  order_delivered:  { icon: '📦', color: '#10B981' },
  order_shipped:    { icon: '🚚', color: '#8B5CF6' },
  order_confirmed:  { icon: '✅', color: '#3B82F6' },
  promo:            { icon: '🏷️', color: '#FFD700' },
  promotion:        { icon: '🏷️', color: '#FFD700' },
  low_stock:        { icon: '⚠️', color: '#F59E0B' },
  welcome:          { icon: '🎉', color: '#0e947a' },
}

// Format a backend promotion into a display notification
function shapePromo(p) {
  const isRead    = !!p.read_at
  const isDismiss = !!p.dismissed_at
  const createdAt = p.created_at ? new Date(p.created_at) : new Date()
  const diffMs    = Date.now() - createdAt.getTime()
  const diffMin   = Math.floor(diffMs / 60000)
  let timeLabel
  if (diffMin < 2)        timeLabel = 'just now'
  else if (diffMin < 60)  timeLabel = `${diffMin}m ago`
  else if (diffMin < 1440) timeLabel = `${Math.floor(diffMin / 60)}h ago`
  else if (diffMin < 10080) timeLabel = `${Math.floor(diffMin / 1440)}d ago`
  else                    timeLabel = `${Math.floor(diffMin / 10080)}w ago`

  return {
    id:         p.id,
    type:       'promo',
    title:      p.title || 'Promotion',
    body:       p.description || p.message || '',
    time:       timeLabel,
    read:       isRead,
    dismissed:  isDismiss,
    code:       p.promo_code || null,
    raw:        p,
  }
}

export function Notifications() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [notifs, setNotifs]   = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  const load = useCallback(async () => {
    if (!isAuthenticated) { setLoading(false); return }
    try {
      const data = await promotionService.getAll()
      const list  = Array.isArray(data) ? data : (data?.promotions ?? data?.items ?? [])
      // Filter out dismissed; shape each
      setNotifs(list.filter(p => !p.dismissed_at).map(shapePromo))
    } catch (e) {
      setError('Could not load notifications')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => { load() }, [load])

  const unreadCount = notifs.filter(n => !n.read).length

  const markRead = async (id) => {
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    try { await promotionService.markRead(id) } catch { /* silent */ }
  }

  const markAllRead = () => {
    notifs.filter(n => !n.read).forEach(n => markRead(n.id))
  }

  const dismiss = async (id) => {
    setNotifs(prev => prev.filter(n => n.id !== id))
    try { await promotionService.dismiss(id) } catch { /* silent */ }
  }

  return (
    <div className="page-container bg-surface-dark">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-surface-dark/95 backdrop-blur-md border-b border-surface-border"
        style={{ paddingTop: 'var(--safe-top)' }}>
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
            </button>
            <div className="flex items-center gap-2">
              <span className="font-heading text-white text-lg">Notifications</span>
              {unreadCount > 0 && (
                <span className="bg-primary text-white text-xs font-display font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                  {unreadCount}
                </span>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button onClick={markAllRead}
              className="text-xs text-primary font-display font-semibold active:opacity-70">
              Mark all read
            </button>
          )}
        </div>
      </div>

      <div className="px-4 pt-3 pb-4 space-y-2">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span className="text-4xl mb-3">⚠️</span>
            <p className="text-gray-400 font-display text-sm">{error}</p>
            <button onClick={load} className="mt-4 btn-outline py-2 px-5 text-sm">Retry</button>
          </div>
        ) : notifs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="text-5xl mb-4">🔔</span>
            <p className="text-white font-heading text-lg mb-1">All caught up!</p>
            <p className="text-gray-400 font-display text-sm">No notifications right now.</p>
          </div>
        ) : notifs.map(n => {
          const meta = NOTIF_META[n.type] || NOTIF_META.promo
          return (
            <div
              key={n.id}
              onClick={() => markRead(n.id)}
              className={`relative rounded-2xl border p-4 flex gap-3 active:scale-[0.98] transition-transform cursor-pointer ${
                n.read ? 'bg-surface-card border-surface-border' : 'bg-primary/5 border-primary/30'
              }`}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}33` }}>
                <span className="text-xl">{meta.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={`font-display font-semibold text-sm truncate ${n.read ? 'text-gray-300' : 'text-white'}`}>
                    {n.title}
                  </span>
                  <span className="text-gray-500 text-[11px] font-display flex-shrink-0">{n.time}</span>
                </div>
                <p className="text-gray-400 text-sm font-display mt-0.5 leading-relaxed">{n.body}</p>
                {n.code && (
                  <div className="mt-2 inline-flex items-center gap-1.5 bg-gold/10 border border-gold/30 rounded-lg px-2.5 py-1">
                    <span className="text-gold text-xs font-display font-bold tracking-wider">{n.code}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-center gap-2 ml-1">
                {!n.read && <div className="w-2 h-2 bg-primary rounded-full" />}
                <button
                  onClick={e => { e.stopPropagation(); dismiss(n.id) }}
                  className="text-gray-600 active:text-gray-300 transition-colors mt-1"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6 6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
